import React from 'react';
import { Drawer, IconButton } from '@material-ui/core';
import { Close as CloseIcon, Save as SaveIcon } from '@material-ui/icons';
import { useIntl } from 'react-intl';
import Button from '../../../../../../../ui/canon/Button';
import Card from '../../../../../../../ui/canon/Card';

// Tailwind-based input components
const CurrencyInput = ({ 
  value, 
  onChange, 
  label, 
  placeholder = "$0", 
  disabled = false, 
  className = "",
  error = null 
}) => {
  const [displayValue, setDisplayValue] = React.useState('');
  const [isFocused, setIsFocused] = React.useState(false);

  const formatCurrency = (num) => {
    if (num === null || num === undefined || num === '') return '';
    const numericValue = typeof num === 'string' ? parseFloat(num.replace(/[^0-9.-]/g, '')) : num;
    if (isNaN(numericValue)) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numericValue);
  };

  const parseValue = (str) => {
    if (!str) return null;
    const cleaned = str.replace(/[^0-9.-]/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? null : parsed;
  };

  React.useEffect(() => {
    if (!isFocused) {
      setDisplayValue(value ? formatCurrency(value) : '');
    }
  }, [value, isFocused]);

  const handleFocus = () => {
    setIsFocused(true);
    if (value) {
      setDisplayValue(value.toString());
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    const numericValue = parseValue(displayValue);
    onChange(numericValue);
    setDisplayValue(numericValue ? formatCurrency(numericValue) : '');
  };

  const handleChange = (e) => {
    if (isFocused) {
      const cleaned = e.target.value.replace(/[^0-9.-]/g, '');
      setDisplayValue(cleaned);
      // Also update parent state immediately so Save works without needing to blur first
      const numericValue = parseFloat(cleaned);
      if (!isNaN(numericValue)) {
        onChange(numericValue);
      }
    } else {
      setDisplayValue(e.target.value);
    }
  };

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-[var(--succession-text-primary)] mb-1">
          {label}
        </label>
      )}
      <input
        type="text"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)] sm:text-sm transition-colors ${
          error ? 'border-red-300 text-red-900' : 'border-[var(--succession-border)] text-[var(--succession-text-primary)]'
        } ${disabled ? 'bg-[var(--succession-bg-secondary)] text-[var(--succession-text-muted)] cursor-not-allowed' : 'bg-[var(--succession-bg-primary)]'} ${className}`}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

const PercentageInput = ({ 
  value, 
  onChange, 
  label, 
  placeholder = "0%", 
  disabled = false, 
  className = "",
  min = null,
  max = null,
  error = null 
}) => {
  const [displayValue, setDisplayValue] = React.useState('');
  const [isFocused, setIsFocused] = React.useState(false);

  const formatPercentage = (num) => {
    if (num === null || num === undefined || num === '') return '';
    const numericValue = typeof num === 'string' ? parseFloat(num.replace(/[^0-9.-]/g, '')) : num;
    if (isNaN(numericValue)) return '';
    return `${(numericValue * 100).toFixed(2)}%`;
  };

  const parseValue = (str) => {
    if (!str) return null;
    const cleaned = str.replace(/[^0-9.-]/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? null : parsed / 100;
  };

  const validateValue = (num) => {
    if (num === null || num === undefined) return num;
    let validatedValue = num;
    if (min !== null && num < min) validatedValue = min;
    if (max !== null && num > max) validatedValue = max;
    return validatedValue;
  };

  React.useEffect(() => {
    if (!isFocused) {
      setDisplayValue(value !== null && value !== undefined ? formatPercentage(value) : '');
    }
  }, [value, isFocused]);

  const handleFocus = () => {
    setIsFocused(true);
    if (value !== null && value !== undefined) {
      setDisplayValue((value * 100).toString());
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    const numericValue = parseValue(displayValue);
    if (numericValue !== null) {
      const validatedValue = validateValue(numericValue);
      onChange(validatedValue);
      setDisplayValue(formatPercentage(validatedValue));
    } else {
      onChange(null);
      setDisplayValue('');
    }
  };

  const handleChange = (e) => {
    if (isFocused) {
      const cleaned = e.target.value.replace(/[^0-9.-]/g, '');
      setDisplayValue(cleaned);
      // Also update parent state immediately so Save works without needing to blur first
      const numericValue = parseFloat(cleaned);
      if (!isNaN(numericValue)) {
        const percentValue = numericValue / 100;
        const validatedValue = validateValue(percentValue);
        onChange(validatedValue);
      }
    } else {
      setDisplayValue(e.target.value);
    }
  };

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-[var(--succession-text-primary)] mb-1">
          {label}
        </label>
      )}
      <input
        type="text"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)] sm:text-sm transition-colors ${
          error ? 'border-red-300 text-red-900' : 'border-[var(--succession-border)] text-[var(--succession-text-primary)]'
        } ${disabled ? 'bg-[var(--succession-bg-secondary)] text-[var(--succession-text-muted)] cursor-not-allowed' : 'bg-[var(--succession-bg-primary)]'} ${className}`}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

const CfaEditDrawer = ({
  open,
  onClose,
  editData,
  onInputChange,
  onSave,
  saving,
  saveError,
  // Feature flags
  showOrdinaryTaxes,
  showPurchasePrice,
  showSellerNote,
  showConsultingAgreements,
  showCheckSwap,
  showBranchExpenses,
  showBranchRetention,
  showLifeTimeCapitalGains,
  continuingPaymentsFINRAFootnote,
  // Calculated values
  displayPurchasePrice,
  calculatedPurchasePrice,
  numberOfBuyers,
  formatCurrency
}) => {
  const intl = useIntl();
  // Calculate consulting agreement values
  const consultingPercentOfDeal = (parseFloat(editData.consultingTotalCompensation) / parseFloat(displayPurchasePrice) * 100).toFixed(2);
  const consultingAnnualCompensation = (parseFloat(editData.consultingTotalCompensation) / parseFloat(editData.consultingLength)).toFixed(2);
  
  // Calculate seller note values
  const sellerNotePercentOfDeal = (parseFloat(editData.sellerNoteAmount) / parseFloat(displayPurchasePrice) * 100).toFixed(2);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          backgroundColor: 'var(--succession-bg-secondary)',
        }
      }}
    >
      <div className="w-full max-w-4xl h-full overflow-auto bg-[var(--succession-bg-secondary)]">
        {/* Header */}
        <div className="p-4 bg-[var(--succession-bg-tertiary)] border-b flex justify-between items-center" style={{ color: 'var(--succession-text-primary)' }}>
          <h2 className="text-xl font-semibold" style={{ color: 'var(--succession-text-primary)' }}>{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.heading' })}</h2>
          <button
            onClick={onClose}
            className="hover:opacity-75 transition-colors"
            style={{ color: 'var(--succession-text-primary)' }}
          >
            <CloseIcon />
          </button>
        </div>
        
        <div className="p-6 space-y-8">
          {/* Basic Information - Cash Flow Setup */}
          <Card variant="outlined" size="lg">
            <h3 className="text-lg font-medium text-[var(--succession-text-primary)] mb-4">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.section_cash_flow_setup' })}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--succession-text-primary)] mb-1">
                  {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_analysis_name' })}
                </label>
                <input
                  type="text"
                  value={editData.name || ''}
                  onChange={(e) => onInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--succession-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--succession-text-primary)] mb-1">
                  {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_length_of_cash_flow' })}
                </label>
                <select
                  value={editData.cashFlowLength || ''}
                  onChange={(e) => onInputChange('cashFlowLength', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-[var(--succession-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                >
                  <option value="">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_select_years' })}</option>
                  {[...Array(20).keys()].map((_, index) => (
                    <option key={index + 1} value={index + 1}>
                      {index + 1}
                    </option>
                  ))}
                </select>
              </div>
              {showOrdinaryTaxes && (
                <div>
                  <label className="block text-sm font-medium text-[var(--succession-text-primary)] mb-1">
                    {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_cash_flow_production_shift' })}
                  </label>
                  <input
                    type="number"
                    value={editData.cashFlowProductionShift || ''}
                    onChange={(e) => onInputChange('cashFlowProductionShift', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-[var(--succession-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                  />
                </div>
              )}
              {showPurchasePrice && (
                <div>
                  <CurrencyInput
                    label={intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_purchase_price' })}
                    value={editData.purchasePrice || displayPurchasePrice || ''}
                    onChange={(value) => onInputChange('purchasePrice', value)}
                  />
                  {calculatedPurchasePrice > 0 && (
                    <p className="text-sm text-[var(--succession-text-muted)] mt-1">
                      {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_calculated_from_cash_flow' })} {formatCurrency(calculatedPurchasePrice)}
                    </p>
                  )}
                </div>
              )}
            </div>
          </Card>

          {/* Practice Metrics */}
          <Card variant="outlined" size="lg">
            <h3 className="text-lg font-medium text-[var(--succession-text-primary)] mb-4">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.section_practice_metrics' })}</h3>
            
            {/* Seller Metrics Section */}
            <div className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <CurrencyInput
                  label={intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_seller_t12' })}
                  value={editData.sellerT12Revenue || ''}
                  onChange={(value) => onInputChange('sellerT12Revenue', value)}
                />
                <PercentageInput
                  label={intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_revenue_growth' })}
                  value={editData.sellerFiveYearGrowthRate || ''}
                  onChange={(value) => onInputChange('sellerFiveYearGrowthRate', value)}
                  min={-1}
                  max={3}
                />

                {/* BD Retention for seller */}
                {showBranchRetention && (
                  <PercentageInput
                    label={intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_bd_retention' })}
                    value={editData.bdRetentionRate || ''}
                    onChange={(value) => onInputChange('bdRetentionRate', value)}
                  />
                )}
              </div>
            </div>
            
            {/* Buyer Metrics Section */}
            {[...Array(numberOfBuyers).keys()].map(i => (
              <div key={i} className="mb-4">
                <h4 className="text-md font-medium text-[var(--succession-text-primary)] mb-3">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_buyer' }, { number: i + 1 })}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {showOrdinaryTaxes && <>
                   <CurrencyInput
                      label={intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_t12' })}
                      value={editData[`buyer${i + 1}T12Revenue`] || ''}
                      onChange={(value) => onInputChange(`buyer${i + 1}T12Revenue`, value)}
                    />
                    <PercentageInput
                      label={intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_revenue_growth' })}
                      value={editData[`buyer${i + 1}FiveYearGrowthRate`] || ''}
                      onChange={(value) => onInputChange(`buyer${i + 1}FiveYearGrowthRate`, value)}
                      min={-1}
                      max={3}
                    />
                  </>}
                  {showBranchExpenses && (
                    <>
                      <CurrencyInput
                        label={intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_annual_expenses' })}
                        value={editData[`buyer${i + 1}AnnualExpenses`] || ''}
                        onChange={(value) => onInputChange(`buyer${i + 1}AnnualExpenses`, value)}
                      />
                      <PercentageInput
                        label={intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_expense_growth' })}
                        value={editData[`buyer${i + 1}ExpenseGrowth`] || ''}
                          onChange={(value) => onInputChange(`buyer${i + 1}ExpenseGrowth`, value)}
                        />
                    </>
                  )}
                </div>
              </div>
            ))}
          </Card>

          {/* Tax Rate Assumptions */}
          <Card variant="outlined" size="lg">
            <h3 className="text-lg font-medium text-[var(--succession-text-primary)] mb-4">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.section_tax_rate_assumptions' })}</h3>
            
            {/* Seller Tax Rate */}
            <div className="mb-6">
              <h4 className="font-medium text-[var(--succession-text-primary)] mb-3">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_seller_income' })}</h4>
              <div className="flex items-center space-x-4 mb-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="sellerTaxRate"
                    checked={!editData.sellerManualTaxRate}
                    onChange={() => onInputChange('sellerManualTaxRate', false)}
                    className="mr-2"
                  />
                  {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_dynamic' })}
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="sellerTaxRate"
                    checked={editData.sellerManualTaxRate}
                    onChange={() => onInputChange('sellerManualTaxRate', true)}
                    className="mr-2"
                  />
                  {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_manual' })}
                </label>
              </div>
              {editData.sellerManualTaxRate && (
                <div className="w-32">
                  <PercentageInput
                    value={editData.sellerTaxRate || ''}
                    onChange={(value) => onInputChange('sellerTaxRate', value)}
                  />
                </div>
              )}
            </div>

            {/* Capital Gains */}
            {showLifeTimeCapitalGains && (
              <div className="mb-6">
                <div className="w-48">
                  <PercentageInput
                    label={intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_seller_ltcg' })}
                    value={editData.cashflowSellerLifetimeCapitalGainsTax || ''}
                    onChange={(value) => onInputChange('cashflowSellerLifetimeCapitalGainsTax', value)}
                    min={-1}
                  />
                </div>
              </div>
            )}

            {/* Buyer Tax Rates */}
            {[...Array(numberOfBuyers).keys()].map(i => (
              <div key={i} className="mb-6">
                <h4 className="font-medium text-[var(--succession-text-primary)] mb-3">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_buyer_income' }, { number: i + 1 })}</h4>
                <div className="flex items-center space-x-4 mb-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name={`buyer${i + 1}TaxRate`}
                      checked={!editData[`buyer${i + 1}ManualTaxRate`]}
                      onChange={() => onInputChange(`buyer${i + 1}ManualTaxRate`, false)}
                      className="mr-2"
                    />
                    {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_dynamic' })}
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name={`buyer${i + 1}TaxRate`}
                      checked={editData[`buyer${i + 1}ManualTaxRate`]}
                      onChange={() => onInputChange(`buyer${i + 1}ManualTaxRate`, true)}
                      className="mr-2"
                    />
                    {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_manual' })}
                  </label>
                </div>
                {editData[`buyer${i + 1}ManualTaxRate`] && (
                  <div className="w-32">
                    <PercentageInput
                      value={editData[`buyer${i + 1}TaxRate`] || ''}
                      onChange={(value) => onInputChange(`buyer${i + 1}TaxRate`, value)}
                    />
                  </div>
                )}
              </div>
            ))}
          </Card>

          <hr className="border-[var(--succession-border)]" />

          {/* Deal Structure */}
          <Card variant="outlined" size="lg">
            <h3 className="text-lg font-medium text-[var(--succession-text-primary)] mb-4">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.section_deal_structure' })}</h3>
            
            <div className="space-y-6">
              {/* Down Payment */}
              <div>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="downPayment"
                        checked={editData.buyerHasLumpSumPayment}
                        onChange={() => onInputChange('buyerHasLumpSumPayment', true)}
                        className="mr-2"
                      />
                      {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_yes' })}
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="downPayment"
                        checked={!editData.buyerHasLumpSumPayment}
                        onChange={() => onInputChange('buyerHasLumpSumPayment', false)}
                        className="mr-2"
                      />
                      {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_no' })}
                    </label>
                  </div>
                  <h4 className="font-medium text-[var(--succession-text-primary)]">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_down_payment' })}</h4>
                </div>

                {/* Down Payment Details */}
                {editData.buyerHasLumpSumPayment && (
                  <div className="space-y-4 pl-4 border-l-2 border-[var(--succession-border)]">
                    <CurrencyInput
                      label={intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_lump_sum_amount' })}
                      value={editData.buyerLumpSumAmount || ''}
                      onChange={(value) => onInputChange('buyerLumpSumAmount', value)}
                    />
                    <div>
                      <label className="block text-sm font-medium text-[var(--succession-text-primary)] mb-1">
                        {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_year' })}
                      </label>
                      <select
                        value={editData.buyerLumpSumYear || ''}
                        onChange={(e) => onInputChange('buyerLumpSumYear', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-[var(--succession-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                      >
                        <option value="">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_please_select_year' })}</option>
                        {[...Array(10).keys()].map(year => (
                          <option key={year + 1} value={year + 1}>{year + 1}</option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Financing under Down Payment */}
                    <div className="bg-[var(--succession-bg-secondary)] p-4 rounded-md">
                      <h5 className="font-medium text-[var(--succession-text-primary)] mb-3">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_financing' })}</h5>
                      <div className="space-y-3">
                        <CurrencyInput
                          label={intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_amount_financed' })}
                          value={editData.buyersFinancingAmount || ''}
                          onChange={(value) => onInputChange('buyersFinancingAmount', value)}
                        />
                        <div>
                          <label className="block text-sm font-medium text-[var(--succession-text-primary)] mb-1">
                            {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_financing_years' })}
                          </label>
                          <select
                            value={editData.buyerFinancingYears || ''}
                            onChange={(e) => onInputChange('buyerFinancingYears', parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-[var(--succession-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                          >
                            <option value="">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_please_select_year' })}</option>
                            {[...Array(editData.cashFlowLength || 20).keys()].map(year => (
                              <option key={year + 1} value={year + 1}>{year + 1}</option>
                            ))}
                          </select>
                        </div>
                        <PercentageInput
                          label={intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_financing_interest_rate' })}
                          value={editData.cashFlowFinancingInterestRate || ''}
                          onChange={(value) => onInputChange('cashFlowFinancingInterestRate', value)}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Seller Note */}
              {showSellerNote && (
                <div>
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="sellerNote"
                          checked={editData.hasSellerNote || editData.sellerNoteAmount > 0}
                          onChange={() => onInputChange('hasSellerNote', true)}
                          className="mr-2"
                        />
                        {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_yes' })}
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="sellerNote"
                          checked={!editData.hasSellerNote && !(editData.sellerNoteAmount > 0)}
                          onChange={() => {
                            onInputChange('hasSellerNote', false);
                            onInputChange('sellerNoteAmount', 0);
                          }}
                          className="mr-2"
                        />
                        {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_no' })}
                      </label>
                    </div>
                    <h4 className="font-medium text-[var(--succession-text-primary)]">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_seller_note' })}</h4>
                  </div>

                  {(editData.hasSellerNote || editData.sellerNoteAmount > 0) && (
                    <div className="space-y-4 pl-4 border-l-2 border-[var(--succession-border)]">
                      <CurrencyInput
                        label={intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_seller_note_amount' })}
                        value={editData.sellerNoteAmount || ''}
                        onChange={(value) => onInputChange('sellerNoteAmount', value)}
                      />
                      
                      {!showOrdinaryTaxes && displayPurchasePrice > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-[var(--succession-text-primary)] mb-1">
                            {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_seller_note_pct_of_deal' })}
                          </label>
                          <input
                            type="text"
                            value={isNaN(sellerNotePercentOfDeal) ? '0%' : `${sellerNotePercentOfDeal}%`}
                            disabled
                            className="w-full px-3 py-2 border border-[var(--succession-border)] rounded-md bg-[var(--succession-bg-secondary)] text-[var(--succession-text-muted)]"
                          />
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-[var(--succession-text-primary)] mb-1">
                          {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_seller_note_term_years' })}
                        </label>
                        <select
                          value={editData.sellerNoteTerm || ''}
                          onChange={(e) => onInputChange('sellerNoteTerm', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-[var(--succession-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                        >
                          <option value="">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_please_select_year' })}</option>
                          {[...Array(editData.cashFlowLength || 20).keys()].map(year => (
                            <option key={year + 1} value={year + 1}>{year + 1}</option>
                          ))}
                        </select>
                      </div>
                      <PercentageInput
                        label={intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_seller_note_interest_rate' })}
                        value={editData.sellerNoteInterestRate || ''}
                        onChange={(value) => onInputChange('sellerNoteInterestRate', value)}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Earn Out */}
              <div>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="earnOut"
                        checked={editData.buyerHasContinuingPayments}
                        onChange={() => onInputChange('buyerHasContinuingPayments', true)}
                        className="mr-2"
                      />
                      {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_yes' })}
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="earnOut"
                        checked={!editData.buyerHasContinuingPayments}
                        onChange={() => onInputChange('buyerHasContinuingPayments', false)}
                        className="mr-2"
                      />
                      {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_no' })}
                    </label>
                  </div>
                  <h4 className="font-medium text-[var(--succession-text-primary)]">{showOrdinaryTaxes ? intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_continuing_payments' }) : intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_earn_out' })}</h4>
                </div>

                {editData.buyerHasContinuingPayments && (
                  <div className="space-y-4 pl-4 border-l-2 border-[var(--succession-border)]">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[var(--succession-text-primary)] mb-1">
                          {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_number_of_years' })}
                        </label>
                        <select
                          value={editData.buyerContinuingPaymentYears || ''}
                          onChange={(e) => {
                            const years = parseInt(e.target.value);
                            onInputChange('buyerContinuingPaymentYears', years);
                            // Initialize payments array
                            const payments = Array(years).fill(0);
                            onInputChange('buyerContinuingPayments', payments);
                          }}
                          className="w-full px-3 py-2 border border-[var(--succession-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                        >
                          <option value="">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_please_select_year' })}</option>
                          {[...Array(5).keys()].map(year => (
                            <option key={year + 1} value={year + 1}>{year + 1}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[var(--succession-text-primary)] mb-1">
                          {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_start_year' })}
                        </label>
                        <select
                          value={editData.buyerContinuingPaymentStartYear || ''}
                          onChange={(e) => onInputChange('buyerContinuingPaymentStartYear', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-[var(--succession-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                        >
                          <option value="">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_please_select_year' })}</option>
                          {[...Array(5).keys()].map(year => (
                            <option key={year + 1} value={year + 1}>{year + 1}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    {/* Year-by-year percentages */}
                    {Boolean(editData.buyerContinuingPaymentYears && editData.buyerContinuingPaymentStartYear) && (
                      <div className="space-y-2">
                        <h5 className="font-medium text-[var(--succession-text-primary)]">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_percentage_by_year' })}</h5>
                        {[...Array(editData.buyerContinuingPaymentYears).keys()].map(i => {
                          const year = editData.buyerContinuingPaymentStartYear + i;
                          return (
                            <div key={i} className="flex items-center space-x-2">
                              <span className="w-16 text-sm">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_year_n' }, { year })}</span>
                              <div className="w-32">
                                <PercentageInput
                                  value={editData.buyerContinuingPayments?.[year] || ''}
                                  onChange={(value) => {
                                    const payments = [...(editData.buyerContinuingPayments || [])];
                                    payments[year] = value;
                                    onInputChange('buyerContinuingPayments', payments);
                                  }}
                                  max={i > 0 ? editData.buyerContinuingPayments?.[year - 1] : 1}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                
                {/* FINRA Footnote */}
                {continuingPaymentsFINRAFootnote && editData.buyerHasContinuingPayments && (
                  <div className="mt-4 text-sm text-[var(--succession-text-secondary)]">
                    {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.finra_note' })}
                  </div>
                )}
              </div>

              {/* Fixed Payments */}
              {(Boolean(showOrdinaryTaxes)) && (
                <div>
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="fixedPayments"
                          checked={editData.buyerHasFixedPayments}
                          onChange={() => onInputChange('buyerHasFixedPayments', true)}
                          className="mr-2"
                        />
                        {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_yes' })}
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="fixedPayments"
                          checked={!editData.buyerHasFixedPayments}
                          onChange={() => onInputChange('buyerHasFixedPayments', false)}
                          className="mr-2"
                        />
                        {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_no' })}
                      </label>
                    </div>
                    <h4 className="font-medium text-[var(--succession-text-primary)]">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_fixed_payments' })}</h4>
                  </div>

                  {editData.buyerHasFixedPayments && (
                    <div className="space-y-4 pl-4 border-l-2 border-[var(--succession-border)]">
                      <CurrencyInput
                        label={intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_payment_amount' })}
                        value={editData.buyerFixedPaymentAmount || ''}
                        onChange={(value) => onInputChange('buyerFixedPaymentAmount', value)}
                      />
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-[var(--succession-text-primary)] mb-1">
                            {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_no_of_years' })}
                          </label>
                          <select
                            value={editData.buyerFixedPaymentYears || ''}
                            onChange={(e) => onInputChange('buyerFixedPaymentYears', parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-[var(--succession-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                          >
                            <option value="">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_please_select_year' })}</option>
                            {[...Array(5).keys()].map(i => (
                              <option key={i + 1} value={i + 1}>{i + 1}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[var(--succession-text-primary)] mb-1">
                            {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_start_year' })}
                          </label>
                          <select
                            value={editData.buyerFixedPaymentStartYear || ''}
                            onChange={(e) => onInputChange('buyerFixedPaymentStartYear', parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-[var(--succession-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                          >
                            <option value="">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_please_select_year' })}</option>
                            {[...Array(5).keys()].map(i => (
                              <option key={i + 1} value={i + 1}>{i + 1}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Consulting Agreement */}
              {showConsultingAgreements && (
                <div>
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="consultingAgreement"
                          checked={editData.hasConsultingAgreement || editData.consultingTotalCompensation > 0}
                          onChange={() => onInputChange('hasConsultingAgreement', true)}
                          className="mr-2"
                        />
                        {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_yes' })}
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="consultingAgreement"
                          checked={!editData.hasConsultingAgreement && !(editData.consultingTotalCompensation > 0)}
                          onChange={() => {
                            onInputChange('hasConsultingAgreement', false);
                            onInputChange('consultingTotalCompensation', 0);
                          }}
                          className="mr-2"
                        />
                        {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_no' })}
                      </label>
                    </div>
                    <h4 className="font-medium text-[var(--succession-text-primary)]">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_consulting_agreement' })}</h4>
                  </div>

                  {(editData.hasConsultingAgreement || editData.consultingTotalCompensation > 0) && (
                    <div className="space-y-4 pl-4 border-l-2 border-[var(--succession-border)]">
                      <CurrencyInput
                        label={intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_total_compensation' })}
                        value={editData.consultingTotalCompensation || ''}
                        onChange={(value) => onInputChange('consultingTotalCompensation', value)}
                      />

                      {!showOrdinaryTaxes && displayPurchasePrice > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-[var(--succession-text-primary)] mb-1">
                            {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_consulting_pct_of_deal' })}
                          </label>
                          <input
                            type="text"
                            value={isNaN(consultingPercentOfDeal) ? '0%' : `${consultingPercentOfDeal}%`}
                            disabled
                            className="w-full px-3 py-2 border border-[var(--succession-border)] rounded-md bg-[var(--succession-bg-secondary)] text-[var(--succession-text-muted)]"
                          />
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-[var(--succession-text-primary)] mb-1">
                          {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_consulting_length_years' })}
                        </label>
                        <select
                          value={editData.consultingLength || ''}
                          onChange={(e) => onInputChange('consultingLength', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-[var(--succession-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                        >
                          <option value="">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_please_select_year' })}</option>
                          {[...Array(editData.cashFlowLength || 20).keys()].map(year => (
                            <option key={year + 1} value={year + 1}>{year + 1}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-[var(--succession-text-primary)] mb-1">
                          {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_annual_compensation' })}
                        </label>
                        <input
                          type="text"
                          value={isNaN(consultingAnnualCompensation) ? '$0' : formatCurrency(consultingAnnualCompensation)}
                          disabled
                          className="w-full px-3 py-2 border border-[var(--succession-border)] rounded-md bg-[var(--succession-bg-secondary)] text-[var(--succession-text-muted)]"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Check Swap */}
              {(showCheckSwap || false) && (
                <div>
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="checkSwap"
                          checked={editData.hasCheckSwap}
                          onChange={() => onInputChange('hasCheckSwap', true)}
                          className="mr-2"
                        />
                        {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_yes' })}
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="checkSwap"
                          checked={!editData.hasCheckSwap}
                          onChange={() => onInputChange('hasCheckSwap', false)}
                          className="mr-2"
                        />
                        {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_no' })}
                      </label>
                    </div>
                    <h4 className="font-medium text-[var(--succession-text-primary)]">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_check_swap' })}</h4>
                  </div>

                  {editData.hasCheckSwap && (
                    <div className="space-y-4 pl-4 border-l-2 border-[var(--succession-border)]">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-[var(--succession-text-primary)] mb-1">
                            {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_check_swap_year' })}
                          </label>
                          <select
                            value={editData.checkSwapYear || ''}
                            onChange={(e) => onInputChange('checkSwapYear', parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-[var(--succession-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                          >
                            <option value="">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_please_select_year' })}</option>
                            {[...Array(editData.cashFlowLength || 10).keys()].map(i => (
                              <option key={i + 1} value={i + 1}>{i + 1}</option>
                            ))}
                          </select>
                        </div>
                        <CurrencyInput
                          label={intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_annual_bonus' })}
                          value={editData.checkSwapAnnualBonus || ''}
                          onChange={(value) => onInputChange('checkSwapAnnualBonus', value)}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-[var(--succession-text-primary)] mb-1">
                            {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_loan_end_year' })}
                          </label>
                          <select
                            value={editData.checkSwapLoanEndYear || ''}
                            onChange={(e) => onInputChange('checkSwapLoanEndYear', parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-[var(--succession-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                          >
                            <option value="">{intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_please_select_year' })}</option>
                            {[...Array(editData.cashFlowLength || 10).keys()].map(i => (
                              <option key={i + 1} value={i + 1}>{i + 1}</option>
                            ))}
                          </select>
                        </div>
                        <CurrencyInput
                          label={intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.label_loan_repayment_amount' })}
                          value={editData.checkSwapLoanRepaymentAmount || ''}
                          onChange={(value) => onInputChange('checkSwapLoanRepaymentAmount', value)}
                        />
                      </div>
                    </div>
                )}
              </div>
              )}
            </div>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-[var(--succession-border)]">
            <Button
              variant="outline"
              onClick={onClose}
            >
              {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.button_cancel' })}
            </Button>
            <Button
              variant="primary"
              onClick={onSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <svg className="w-4 h-4 mr-2 inline animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.button_saving' })}
                </>
              ) : (
                <>
                  <SaveIcon className="w-4 h-4 mr-2 inline" />
                  {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.button_save_changes' })}
                </>
              )}
            </Button>
          </div>
          
          {/* Error Message */}
          {saveError && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    {intl.formatMessage({ id: 'succession.cash_flow_analysis.edit_drawer.error_saving' })}
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    {saveError}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default CfaEditDrawer; 